/**
 * @file ProductsView.tsx
 *
 * Products tab screen. Fetches and renders the product list with debounced
 * search, stock and category filters, a grid/list toggle, sortable list view,
 * and pagination. Selecting a product opens a detail drawer; the per-product
 * contextual menu drives view/edit/duplicate/delete. Create, edit, and
 * duplicate navigate to the product form route; delete is confirmed inline.
 *
 * State (search, filters, page, sort, menu/drawer/confirm) lives here so a
 * single menu and drawer serve both grid cards and list rows.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Loader } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { notifyError, notifySuccess } from '../feedback/toast';
import { useClickOutside } from '../hooks/use-click-outside';
import { useMediaQuery } from '../hooks/use-media-query';
import { useProductsStore } from '../stores/products.store';
import { useSettingsStore } from '../stores/settings.store';
import { PRODUCTS_PER_PAGE } from '../api/products';
import type { ShopProduct } from '../api/shop-types';
import { EmptyState } from '../components/EmptyState';
import { ConfirmModal } from '../components/ConfirmModal';
import { ProductCard } from './ProductCard';
import { ProductsToolbar } from './ProductsToolbar';
import { ContextMenu } from '../components/ContextMenu';
import { ProductsTable } from './ProductsTable';
import { ProductDetailsDrawer } from './ProductDetailsDrawer';
import {
  CONTEXT_MENU_HEIGHT,
  CONTEXT_MENU_VIEWPORT_MARGIN,
  CONTEXT_MENU_WIDTH,
  PRODUCT_MENU_ACTIONS,
  STATUS_FILTER_KEYS,
  STATUS_FILTER_LABELS,
  STOCK_KEYS,
  STOCK_LABELS,
  type ProductMenuAction,
  type SortColumn,
  type SortOrder,
  type ViewMode,
} from './product-list';
import shared from '../shared.module.css';
import styles from './ProductsView.module.css';

interface MenuPosition {
  top?: number;
  left?: number;
}

/**
 * Renders the Products tab.
 *
 * @returns The products screen.
 */
export function ProductsView() {
  const intl = useIntl();
  const navigate = useNavigate();

  const products = useProductsStore((s) => s.products);
  const categories = useProductsStore((s) => s.categories);
  const loading = useProductsStore((s) => s.loading);
  const productsTotal = useProductsStore((s) => s.productsTotal);
  const productsTotalPages = useProductsStore((s) => s.productsTotalPages);
  const fetchProducts = useProductsStore((s) => s.fetchProducts);
  const fetchCategories = useProductsStore((s) => s.fetchCategories);
  const deleteProduct = useProductsStore((s) => s.deleteProduct);
  const loadOne = useProductsStore((s) => s.loadOne);
  const currentProduct = useProductsStore((s) => s.current);

  const currencySymbol = useSettingsStore((s) => s.currencySymbol);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilters, setCategoryFilters] = useState<string[]>([]);
  const [stockFilters, setStockFilters] = useState<string[]>([]);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortColumn | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  const [menuProductId, setMenuProductId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);

  const [selectedProduct, setSelectedProduct] = useState<ShopProduct | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ShopProduct | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const drawerRef = useRef<HTMLDivElement>(null);
  const lastSelectedRef = useRef<ShopProduct | null>(null);

  const isMobile = useMediaQuery('Mobile');

  useEffect(() => {
    if (selectedProduct) {
      lastSelectedRef.current = selectedProduct;
    }
  }, [selectedProduct]);

  // The stock filter drives the CE `availability` param (server-side) — a single in/out
  // choice; a mixed or empty selection means no availability filter.
  const availability =
    stockFilters.length === 1 && (stockFilters[0] === 'instock' || stockFilters[0] === 'outofstock')
      ? stockFilters[0]
      : undefined;

  useEffect(() => {
    fetchProducts({
      page,
      perPage: PRODUCTS_PER_PAGE,
      search: debouncedSearch,
      category: categoryFilters.join(','),
      status: statusFilters.join(','),
      availability,
      ...(sortBy && { orderby: sortBy, order: sortOrder }),
    });
  }, [
    fetchProducts,
    page,
    debouncedSearch,
    categoryFilters,
    statusFilters,
    availability,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useClickOutside(drawerRef, () => setSelectedProduct(null), !!selectedProduct);

  const closeMenu = (): void => {
    setMenuProductId(null);
    setMenuPosition(null);
  };

  const handleMoreClick = (
    product: ShopProduct,
    triggerEl: HTMLButtonElement,
  ): void => {
    if (menuProductId === product.id) {
      closeMenu();
      return;
    }
    if (isMobile) {
      setMenuPosition({ top: undefined, left: undefined });
    } else {
      const rect = triggerEl.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const top =
        spaceBelow >= CONTEXT_MENU_HEIGHT
          ? rect.bottom + 4
          : rect.top - CONTEXT_MENU_HEIGHT - 4;
      const left = Math.max(
        CONTEXT_MENU_VIEWPORT_MARGIN,
        Math.min(
          rect.right - CONTEXT_MENU_WIDTH,
          window.innerWidth - CONTEXT_MENU_WIDTH - CONTEXT_MENU_VIEWPORT_MARGIN,
        ),
      );
      setMenuPosition({ top, left });
    }
    setMenuProductId(product.id);
  };

  const handleSort = (column: SortColumn): void => {
    if (sortBy === column) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const openCreate = (): void => {
    navigate('/products/new');
  };
  const openEdit = (product: ShopProduct): void => {
    setSelectedProduct(null);
    navigate(`/products/${product.id}/edit`);
  };
  const openDuplicate = (product: ShopProduct): void => {
    setSelectedProduct(null);
    navigate(`/products/${product.id}/duplicate`);
  };

  // Open the quick-view drawer for a product (toggling it shut if already shown),
  // and fetch the full product so the drawer shows the detail the list summary omits.
  const openDetails = (product: ShopProduct): void => {
    const next = selectedProduct?.id === product.id ? null : product;
    setSelectedProduct(next);
    if (next) {
      void loadOne(next.id);
    }
  };

  const handleMenuSelect = (action: ProductMenuAction): void => {
    const product = products.find((p) => p.id === menuProductId);
    closeMenu();
    if (!product) {
      return;
    }
    if (action === 'view') {
      setSelectedProduct(product);
      void loadOne(product.id);
    } else if (action === 'edit') {
      openEdit(product);
    } else if (action === 'duplicate') {
      openDuplicate(product);
    } else {
      setDeleteError(null);
      setConfirmDelete(product);
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    setDeleting(id);
    setDeleteError(null);
    const ok = await deleteProduct(id);
    setDeleting(null);
    if (!ok) {
      setDeleteError(intl.formatMessage(adminMessages.productsDeleteError));
      notifyError(intl.formatMessage(adminMessages.toastError));
      return;
    }
    notifySuccess(intl.formatMessage(adminMessages.toastDeleted));
    setConfirmDelete(null);
    setSelectedProduct(null);
    await fetchProducts({
      page,
      perPage: PRODUCTS_PER_PAGE,
      search: debouncedSearch,
      category: categoryFilters.join(','),
      status: statusFilters.join(','),
      availability,
      ...(sortBy && { orderby: sortBy, order: sortOrder }),
    });
  };

  // Filtering is server-side (paged over the whole catalog), so the loaded page is the list —
  // no client-side re-filtering (which previously only saw the current 50-row page).
  const stockOptions = STOCK_KEYS.map((key) => ({
    value: key,
    label: intl.formatMessage(STOCK_LABELS[key]),
  }));

  const statusOptions = STATUS_FILTER_KEYS.map((key) => ({
    value: key,
    label: intl.formatMessage(STATUS_FILTER_LABELS[key]),
  }));

  const categoryOptions = categories.map((cat) => ({
    value: cat.id,
    label: cat.name,
  }));

  const effectiveTotalPages =
    productsTotalPages || Math.ceil(productsTotal / PRODUCTS_PER_PAGE) || 1;

  const hasActiveFilters =
    debouncedSearch !== '' ||
    stockFilters.length > 0 ||
    statusFilters.length > 0 ||
    categoryFilters.length > 0;

  if (loading && products.length === 0) {
    return (
      <div className="gv-flex gv-items-center gv-justify-center gv-h-full gv-w-full">
        <Loader>
          <FormattedMessage {...adminMessages.productsLoading} />
        </Loader>
      </div>
    );
  }

  const selectedDetail = selectedProduct ?? lastSelectedRef.current;
  const drawerProduct =
    currentProduct && selectedDetail && currentProduct.id === selectedDetail.id
      ? currentProduct
      : selectedDetail;

  return (
    <div className={`${styles.container} ${shared.outerContainer}`}>
      <div className={styles.productToolbar}>
        <h2 className={`gv-heading-lg ${styles.title}`}>
          <FormattedMessage {...adminMessages.productsTitle} />
        </h2>
        <div className="gv-header-actions">
          <button
            className="gv-button gv-button-primary gv-max-tab-sm-mode-condensed"
            type="button"
            data-testid="products-add-button"
            onClick={openCreate}
          >
            <Icon name="add" size="small" />
            <FormattedMessage {...adminMessages.productsAdd} />
          </button>
        </div>
      </div>

      {!hasActiveFilters && products.length === 0 && !loading ? (
        <EmptyState
          testId="products-empty"
          title={intl.formatMessage(adminMessages.productsEmptyTitle)}
          description={intl.formatMessage(adminMessages.productsEmptyDescription)}
          action={{
            label: intl.formatMessage(adminMessages.productsAdd),
            onClick: openCreate,
          }}
          labelPrefix={<Icon name="add" size="small" />}
        />
      ) : (
        <div className="gv-flex gv-flex-col gv-gap-md">
          <ProductsToolbar
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            stockOptions={stockOptions}
            stockSelected={stockFilters}
            onStockApply={(values) => {
              setStockFilters(values);
              setPage(1);
            }}
            statusOptions={statusOptions}
            statusSelected={statusFilters}
            onStatusApply={(values) => {
              setStatusFilters(values);
              setPage(1);
            }}
            categoryOptions={categoryOptions}
            categorySelected={categoryFilters}
            onCategoryApply={(values) => {
              setCategoryFilters(values);
              setPage(1);
            }}
            search={search}
            onSearchChange={setSearch}
          />

          {products.length === 0 && !loading ? (
            <EmptyState
              testId="products-no-results"
              title={intl.formatMessage(adminMessages.productsNoResultsTitle)}
              description={intl.formatMessage(
                adminMessages.productsNoResultsDescription,
              )}
            />
          ) : viewMode === 'grid' ? (
            <div
              className={styles.productsGrid}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  currencySymbol={currencySymbol}
                  menuOpen={menuProductId === product.id}
                  onMoreClick={handleMoreClick}
                  onCardClick={openDetails}
                />
              ))}
            </div>
          ) : (
            <ProductsTable
              products={products}
              currencySymbol={currencySymbol}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              menuProductId={menuProductId}
              onMore={handleMoreClick}
              selectedId={selectedProduct?.id ?? null}
              onSelect={openDetails}
              page={page}
              totalPages={effectiveTotalPages}
              onPage={setPage}
            />
          )}
        </div>
      )}

      {menuProductId !== null && menuPosition && (
        <ContextMenu
          items={PRODUCT_MENU_ACTIONS}
          position={menuPosition}
          isMobile={isMobile}
          testIdPrefix="product"
          onSelect={handleMenuSelect}
          onClose={closeMenu}
        />
      )}

      <ProductDetailsDrawer
        ref={drawerRef}
        product={drawerProduct}
        isOpen={!!selectedProduct}
        currencySymbol={currencySymbol}
        onClose={() => setSelectedProduct(null)}
        onEdit={openEdit}
        onDelete={(product) => {
          setDeleteError(null);
          setConfirmDelete(product);
        }}
      />

      {confirmDelete && (
        <ConfirmModal
          title={intl.formatMessage(adminMessages.productsDeleteTitle)}
          message={intl.formatMessage(adminMessages.productsDeleteMessage, {
            name: confirmDelete.name,
          })}
          confirmLabel={intl.formatMessage(adminMessages.productsDelete)}
          cancelLabel={intl.formatMessage(adminMessages.productsFilterCancel)}
          loadingLabel={intl.formatMessage(adminMessages.productsDeleteLoading)}
          confirmVariant="destructive"
          loading={deleting === confirmDelete.id}
          errorMessage={deleteError ?? undefined}
          onPrimaryClick={() => void handleDelete(confirmDelete.id)}
          onSecondaryClick={() => setConfirmDelete(null)}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
